
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';

const STORAGE_KEY_MOBILE = 'pwa_install_dismissed';
const STORAGE_KEY_PC = 'bookmark_prompt_dismissed';

function useDeviceType() {
  const ua = navigator.userAgent;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(ua) || !window.matchMedia('(hover: hover)').matches;
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMac = /Macintosh|Mac OS X/i.test(ua) && !isMobile;
  return { isMobile, isIOS, isAndroid, isMac };
}

function isPWAInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true;
}

// iOS向け手順モーダル
function IOSInstallModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-gray-800">ホーム画面に追加する方法</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="space-y-4">
          {[
            { step: 1, icon: 'ri-share-forward-line', text: 'Safari下部の「共有」ボタン（□↑）をタップ' },
            { step: 2, icon: 'ri-add-box-line', text: '「ホーム画面に追加」を選択' },
            { step: 3, icon: 'ri-check-line', text: '「追加」をタップして完了！' },
          ].map(({ step, icon, text }) => (
            <div key={step} className="flex items-start gap-3">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-500 text-white rounded-full text-xs font-bold flex-shrink-0">
                {step}
              </div>
              <div className="flex items-center gap-2 flex-1">
                <div className="w-6 h-6 flex items-center justify-center text-teal-600">
                  <i className={`${icon} text-base`}></i>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 bg-teal-50 rounded-xl p-3 border border-teal-100">
          <p className="text-xs text-teal-700 text-center">
            <i className="ri-information-line mr-1"></i>
            Safariブラウザからのみ追加できます
          </p>
        </div>
        <button
          onClick={onClose}
          className="mt-4 w-full py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          わかりました
        </button>
      </div>
    </div>
  );
}

export default function InstallPromptBanner() {
  const { user } = useAuth();
  const { isMobile, isIOS, isAndroid, isMac } = useDeviceType();
  const [showMobileBanner, setShowMobileBanner] = useState(false);
  const [showPCBanner, setShowPCBanner] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const deferredPromptRef = useRef<any>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    if (isPWAInstalled()) return;

    const timer = setTimeout(() => {
      if (isMobile) {
        if (!localStorage.getItem(STORAGE_KEY_MOBILE)) {
          setShowMobileBanner(true);
          setTimeout(() => setVisible(true), 50);
        }
      } else {
        if (!localStorage.getItem(STORAGE_KEY_PC)) {
          setShowPCBanner(true);
          setTimeout(() => setVisible(true), 50);
        }
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [user, isMobile]);

  const dismissMobile = (permanent: boolean) => {
    setVisible(false);
    setTimeout(() => setShowMobileBanner(false), 400);
    if (permanent) localStorage.setItem(STORAGE_KEY_MOBILE, '1');
  };

  const dismissPC = () => {
    setVisible(false);
    setTimeout(() => setShowPCBanner(false), 400);
    localStorage.setItem(STORAGE_KEY_PC, '1');
  };

  const handleAndroidInstall = async () => {
    if (deferredPromptRef.current) {
      deferredPromptRef.current.prompt();
      const { outcome } = await deferredPromptRef.current.userChoice;
      if (outcome === 'accepted') {
        dismissMobile(true);
      }
      deferredPromptRef.current = null;
    } else {
      setShowIOSModal(true);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* モバイル向けバナー（画面下部） */}
      {showMobileBanner && (
        <div
          className={`fixed bottom-0 left-0 right-0 z-[9990] transition-transform duration-400 ease-out ${
            visible ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="bg-white border-t border-gray-200 shadow-2xl px-4 py-4 safe-area-bottom">
            <div className="max-w-lg mx-auto">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex-shrink-0 shadow-md">
                  <i className="ri-smartphone-line text-white text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-800">ホーム画面に追加しませんか？</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    アプリのようにすぐ開けて、学習記録がもっと便利になります
                  </p>
                </div>
                <button
                  onClick={() => dismissMobile(false)}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={isIOS ? () => setShowIOSModal(true) : handleAndroidInstall}
                  className="flex-1 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap shadow-md shadow-teal-200/40 flex items-center justify-center gap-1.5"
                >
                  <i className="ri-add-line text-base"></i>
                  ホーム画面に追加
                </button>
                <button
                  onClick={() => dismissMobile(true)}
                  className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-xs font-medium cursor-pointer whitespace-nowrap"
                >
                  今後表示しない
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PC向けバナー（画面上部） */}
      {showPCBanner && (
        <div
          className={`fixed top-[72px] left-0 right-0 z-[9990] transition-transform duration-400 ease-out ${
            visible ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="bg-gradient-to-r from-teal-500 to-emerald-600 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                  <i className="ri-bookmark-line text-white text-sm"></i>
                </div>
                <p className="text-sm text-white font-medium">
                  このサイトをお気に入りに追加して、いつでもすぐにアクセスしましょう！
                  <span className="ml-2 text-teal-100 text-xs font-normal">
                    {isMac ? '⌘+D' : 'Ctrl+D'} で追加できます
                  </span>
                </p>
              </div>
              <button
                onClick={dismissPC}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
              >
                <i className="ri-close-line text-sm"></i>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iOS手順モーダル */}
      {showIOSModal && <IOSInstallModal onClose={() => setShowIOSModal(false)} />}
    </>
  );
}
